import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  useCampaignOutcomes,
  useFetchCampaignSteps,
  useReferenceObjectAdmin,
} from '../../../../hooks/campaign-hooks';
import {
  Campaign,
  CampaignOutcome,
  CampaignQuestion,
  OUTCOME_CONTEXTS,
  OutcomeContext,
} from '../../../../support/campaigns';
import { suggestPoolQuestions } from '../ai/campaignFlows';
import { GhostButton, PrimaryButton, SectionHeader, builderStyles } from './primitives';
import { byOrder, nextOrderOf, signature } from './builderShared';
import { buildReferenceUsage, questionUsage, WhereUsed } from './referenceUsage';

/**
 * Admin editor for a campaign's AI question pool (`CampaignQuestion`) — the set the AI draws from
 * during a delivery, split from the Outcomes editor into its own reference-object tab.
 *
 * Rows are NOT edited inline: each shows a read-only summary and an Edit control that expands its
 * config (question text, the outcome it explores, customer/internal context) plus a "where it's used"
 * section listing the steps that surface it (resolved from the pool row's `metadata.source` link back
 * to its content-block question — see `referenceUsage`). "✨ Suggest questions" asks the model for
 * grounded probing questions off the current outcomes; nothing is written until the author keeps one.
 *
 * Persistence is the builder's usual full-record PUT: rows create immediately (so they get an id),
 * text flushes on blur, selects flush on change; a local mirror keeps typing responsive.
 */
export function QuestionPoolEditor(props: { campaign: Campaign | null; canWrite: boolean }): JSX.Element {
  const { campaign, canWrite } = props;

  // Global (library) mode = no campaign: manage every global question directly (create/edit/delete).
  // Campaign mode = a campaign references a subset: `admin.linked` is that subset, `admin.unlinked`
  // backs the "attach existing" picker, and `createLinked`/`detach` create-and-link / detach without
  // deleting the (possibly shared) global question. See PersonasEditor for the same pattern.
  const global = campaign == null;
  const { data: outcomeRows = [] } = useCampaignOutcomes(campaign?.id);
  const admin = useReferenceObjectAdmin<CampaignQuestion>('question', campaign?.id);
  const questionRows = global ? admin.all : admin.linked;
  const isLoading = admin.isLoading;
  const { data: steps = [] } = useFetchCampaignSteps(campaign?.id);
  const questionMut = admin;

  const [attachAnchor, setAttachAnchor] = React.useState<null | HTMLElement>(null);
  const [questions, setQuestions] = React.useState<CampaignQuestion[]>([]);
  const questionsSig = signature(questionRows);
  React.useEffect(() => {
    setQuestions(byOrder(questionRows));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsSig]);

  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());
  const toggleExpanded = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const [suggesting, setSuggesting] = React.useState(false);
  const [suggestNote, setSuggestNote] = React.useState<string | null>(null);

  const outcomes = React.useMemo(() => byOrder(outcomeRows), [outcomeRows]);
  const stepsSig = signature(steps);
  const usage = React.useMemo(
    () => buildReferenceUsage(steps),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepsSig],
  );
  const contextLabel = (c: OutcomeContext) =>
    OUTCOME_CONTEXTS.find((o) => o.value === c)?.label ?? c;
  const outcomeTitle = (id: number | null) =>
    id == null ? 'General pool' : outcomes.find((o) => o.id === id)?.title || 'General pool';

  const patchLocal = (id: number, next: Partial<CampaignQuestion>) =>
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, ...next } : q)));

  const save = (row: CampaignQuestion, next: Partial<CampaignQuestion>) => {
    patchLocal(row.id, next);
    questionMut.update.mutate({ ...row, ...next, id: row.id });
  };

  const addQuestion = (seed?: Partial<CampaignQuestion>) => {
    const fields = {
      outcome: null,
      text: '',
      context: 'customer' as OutcomeContext,
      order: nextOrderOf(questions),
      hidden: false,
      metadata: {},
      ...seed,
    };
    // Global mode creates a bare global question; campaign mode creates-and-links it to the campaign.
    if (global) void admin.create(fields);
    else void admin.createLinked(fields);
  };

  const attachQuestion = (id: number) => admin.attachExisting(id);

  // Campaign mode: "Remove" detaches this campaign's reference; the global question (which another
  // campaign may reference) is left intact. Global mode: delete the global question everywhere (with
  // confirmation, since it is removed from every campaign that references it).
  const removeQuestion = (row: CampaignQuestion) => {
    if (global) {
      const ok = window.confirm(
        'Delete this question from the global library? It will be removed from every campaign that references it.',
      );
      if (!ok) return;
      setQuestions((prev) => prev.filter((q) => q.id !== row.id));
      admin.removeGlobal(row.id);
      return;
    }
    setQuestions((prev) => prev.filter((q) => q.id !== row.id));
    admin.detach(row.id);
  };

  const runSuggest = async () => {
    // Suggestions are grounded on a campaign's outcomes; the global library has no campaign context.
    if (campaign == null) return;
    const usable = outcomes.filter((o) => o.title.trim());
    if (!usable.length) {
      setSuggestNote('Add at least one outcome with a title first — suggestions are grounded on them.');
      return;
    }
    setSuggesting(true);
    setSuggestNote(null);
    try {
      const result = await suggestPoolQuestions({
        campaign,
        outcomes: usable.map((o: CampaignOutcome) => ({
          title: o.title,
          description: o.description,
          context: o.context,
        })),
        existingQuestions: questions.map((q) => q.text).filter(Boolean),
      });
      if (!result || !result.length) {
        setSuggestNote('No suggestions came back. You can still add questions by hand.');
        return;
      }
      const byTitle = new Map(usable.map((o) => [o.title.trim().toLowerCase(), o.id]));
      let base = nextOrderOf(questions);
      for (const s of result) {
        const linked = s.outcome_title ? byTitle.get(s.outcome_title.trim().toLowerCase()) ?? null : null;
        addQuestion({ text: s.text, outcome: linked, context: s.context, order: base++ });
      }
      setSuggestNote(`Added ${result.length} suggested question${result.length === 1 ? '' : 's'} — edit or remove any.`);
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div className={builderStyles.contentCard}>
      <SectionHeader
        compact
        title="Question pool"
        subtitle="The questions the AI draws from during a delivery — skipping achieved outcomes and probing why an unmet one is unmet. Customer-facing and internal are kept separate so reporting never mixes them."
        actions={
          canWrite ? (
            <PrimaryButton onClick={runSuggest} disabled={suggesting || questionMut.isSaving}>
              {suggesting ? 'Suggesting…' : '✨ Suggest questions'}
            </PrimaryButton>
          ) : undefined
        }
      />

      {suggestNote && (
        <Alert severity="info" sx={{ mb: 1.25 }} onClose={() => setSuggestNote(null)}>
          {suggestNote}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          {questions.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No questions yet. Add them by hand or let the AI suggest a starting set from the outcomes.
            </Typography>
          )}

          <Stack spacing={1}>
            {questions.map((row) => {
              const source = (row.metadata as { source?: { category?: string; key?: string } } | null)
                ?.source;
              const usedIn = questionUsage(usage, source);
              const isOpen = expanded.has(row.id);
              return (
                <Box
                  key={row.id}
                  sx={{
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 1,
                    p: 1,
                    opacity: row.hidden ? 0.55 : 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.text.trim() || <em>(untitled question)</em>}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }} useFlexGap flexWrap="wrap">
                        <Chip size="small" variant="outlined" label={contextLabel(row.context)} />
                        <Chip size="small" variant="outlined" label={outcomeTitle(row.outcome)} />
                        {global ? (
                          <Typography variant="caption" color="text.secondary">
                            · used by {admin.usageCount(row.id)} campaign
                            {admin.usageCount(row.id) === 1 ? '' : 's'}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            · used in {usedIn.length} step{usedIn.length === 1 ? '' : 's'}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    {canWrite && (
                      <Stack direction="row">
                        <Tooltip title={isOpen ? 'Close' : 'Edit'}>
                          <IconButton size="small" onClick={() => toggleExpanded(row.id)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={row.hidden ? 'Hidden from AI — show' : 'Exclude from AI selection'}>
                          <IconButton size="small" onClick={() => save(row, { hidden: !row.hidden })}>
                            {row.hidden ? (
                              <VisibilityOffIcon fontSize="small" />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={global ? 'Delete question (removes it from all campaigns)' : 'Remove question'}>
                          <IconButton size="small" onClick={() => removeQuestion(row)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>

                  <Collapse in={isOpen} unmountOnExit>
                    <Stack spacing={1} sx={{ mt: 1.25 }}>
                      <TextField
                        label="Question"
                        size="small"
                        fullWidth
                        multiline
                        value={row.text}
                        disabled={!canWrite}
                        onChange={(e) => patchLocal(row.id, { text: e.target.value })}
                        onBlur={() => save(row, { text: row.text })}
                      />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                          select
                          label="Explores outcome"
                          size="small"
                          value={row.outcome == null ? '' : String(row.outcome)}
                          disabled={!canWrite}
                          onChange={(e) =>
                            save(row, {
                              outcome: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                          sx={{ minWidth: 190, flex: 1 }}
                        >
                          <MenuItem value="">General pool</MenuItem>
                          {outcomes
                            .filter((o) => o.title.trim())
                            .map((o) => (
                              <MenuItem key={o.id} value={String(o.id)}>
                                {o.title}
                              </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                          select
                          label="Context"
                          size="small"
                          value={row.context}
                          disabled={!canWrite}
                          onChange={(e) => save(row, { context: e.target.value as OutcomeContext })}
                          sx={{ minWidth: 170 }}
                        >
                          {OUTCOME_CONTEXTS.map((c) => (
                            <MenuItem key={c.value} value={c.value}>
                              {c.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                      {!global && <WhereUsed steps={usedIn} noun="question" />}
                    </Stack>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>

          {canWrite && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <GhostButton onClick={() => addQuestion()} disabled={questionMut.isSaving}>
                + Add question
              </GhostButton>
              {/* "Attach existing" only makes sense inside a campaign; the global library shows all. */}
              {!global && (
                <>
                  <GhostButton
                    onClick={(e) => setAttachAnchor(e.currentTarget)}
                    disabled={questionMut.isSaving || admin.unlinked.length === 0}
                  >
                    Add existing
                  </GhostButton>
                  <Menu
                    anchorEl={attachAnchor}
                    open={Boolean(attachAnchor)}
                    onClose={() => setAttachAnchor(null)}
                  >
                    {admin.unlinked.map((q) => (
                      <MenuItem
                        key={q.id}
                        onClick={() => {
                          attachQuestion(q.id);
                          setAttachAnchor(null);
                        }}
                      >
                        {q.text.trim() || `(untitled question #${q.id})`}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Stack>
          )}
        </>
      )}
    </div>
  );
}

export default QuestionPoolEditor;
